import React, { useEffect, useMemo, useState } from "react";
import { useTranslate } from "@refinedev/core";
import { useForm } from "@refinedev/antd";
import {
    Alert,
    Button,
    Card,
    DatePicker,
    Divider,
    Flex,
    Input,
    Select,
    Spin,
    Switch,
    TimePicker,
    Typography,
    message,
    theme,
} from "antd";
import {
    ClockCircleOutlined,
    CopyOutlined,
    DeleteOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
    ITenant,
    ITenantClosingDate,
    ITenantTimeRange,
    TENANT_WEEKDAYS,
    TenantWeekday,
} from "../../../../models/tenant";
import {
    DEFAULT_OPENING_HOURS,
    DEFAULT_TIMEZONE,
    emptyWeek,
    validateWeek,
    type WeekValidationError,
} from "../../../../shared/openingHours";
import { generateRandomString } from "../../../../shared/utils";
import { TENANT_BUSINESS_HOURS_UPDATED_EVENT } from "../../../../hooks/tenant";
import { usePageStyles } from "../../../../shared/styles";

const { Title, Text } = Typography;

const TIME_FORMAT = "HH:mm";
const DATE_FORMAT = "YYYY-MM-DD";

/** Range added when a day is switched on or a second window is appended. */
const NEW_RANGE: ITenantTimeRange = { start: "09:00", end: "18:00" };

/** Shown when the browser is too old for `Intl.supportedValuesOf`. */
const FALLBACK_TIMEZONES = [
    "Europe/Paris",
    "Europe/Brussels",
    "Europe/Madrid",
    "Europe/Lisbon",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Zurich",
    "Africa/Tunis",
    "Africa/Algiers",
    "Africa/Casablanca",
    "America/Montreal",
    "UTC",
];

/**
 * "08:00" -> a dayjs at that time today. Built by hand rather than
 * `dayjs(value, "HH:mm")` so it does not depend on the customParseFormat plugin
 * that antd's picker happens to register on the shared dayjs singleton.
 */
const parseTime = (time: string): Dayjs => {
    const [hours, minutes] = time.split(":").map(Number);
    return dayjs().hour(hours).minute(minutes).second(0).millisecond(0);
};

const getTimezoneOptions = (): string[] => {
    // `supportedValuesOf` is ES2022 and not in this project's TS lib.
    const supported = (Intl as unknown as {
        supportedValuesOf?: (key: string) => string[];
    }).supportedValuesOf?.("timeZone");
    return supported?.length ? supported : FALLBACK_TIMEZONES;
};

/**
 * Tenant opening hours and holiday closures.
 *
 * The schedule is held in local state rather than antd `Form.List` fields: the
 * shape is a nested record of dayjs pairs, and the manual round-trip to
 * "HH:mm" / "YYYY-MM-DD" strings is far easier to follow this way. Saving goes
 * through the same `useForm` on `tenants/my-tenant` that the General tab uses.
 */
export const OpeningHoursTab: React.FC = () => {
    const _t = useTranslate();
    const { token } = theme.useToken();
    const pageStyles = usePageStyles();

    const { formLoading, query, onFinish } = useForm<ITenant>({
        action: "edit",
        resource: "tenants",
        id: "my-tenant",
        redirect: false,
        onMutationSuccess: () => {
            message.success(_t("openingHours.admin.saveSuccess"));
            // Refresh the header pill and the order-page notice in place.
            window.dispatchEvent(new Event(TENANT_BUSINESS_HOURS_UPDATED_EVENT));
        },
        onMutationError: () => {
            message.error(_t("openingHours.admin.saveError"));
        },
    });

    const tenant = query?.data?.data;

    const [enabled, setEnabled] = useState(false);
    const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
    const [week, setWeek] = useState<Record<TenantWeekday, ITenantTimeRange[]>>(
        emptyWeek(),
    );
    const [closures, setClosures] = useState<ITenantClosingDate[]>([]);
    const [errors, setErrors] = useState<WeekValidationError[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!tenant) return;
        const hours = tenant.openingHours;
        setEnabled(hours?.enabled ?? false);
        setTimezone(hours?.timezone || DEFAULT_TIMEZONE);
        // A tenant that never configured anything starts from the common
        // weekday schedule rather than an empty grid.
        setWeek(
            hours?.week
                ? { ...emptyWeek(), ...hours.week }
                : { ...DEFAULT_OPENING_HOURS.week },
        );
        setClosures(tenant.closingDates ?? []);
    }, [tenant]);

    const timezoneOptions = useMemo(
        () => getTimezoneOptions().map((zone) => ({ value: zone, label: zone })),
        [],
    );

    const errorForDay = (day: TenantWeekday) =>
        errors.find((error) => error.day === day);

    const setDayRanges = (day: TenantWeekday, ranges: ITenantTimeRange[]) => {
        setWeek((current) => ({ ...current, [day]: ranges }));
        setErrors([]);
    };

    const toggleDay = (day: TenantWeekday, isOpen: boolean) => {
        setDayRanges(day, isOpen ? [{ ...NEW_RANGE }] : []);
    };

    const updateRange = (
        day: TenantWeekday,
        index: number,
        value: [Dayjs | null, Dayjs | null] | null,
    ) => {
        const [start, end] = value ?? [null, null];
        if (!start || !end) return;
        setDayRanges(
            day,
            week[day].map((range, rangeIndex) =>
                rangeIndex === index
                    ? { start: start.format(TIME_FORMAT), end: end.format(TIME_FORMAT) }
                    : range,
            ),
        );
    };

    const addRange = (day: TenantWeekday) => {
        setDayRanges(day, [...week[day], { ...NEW_RANGE }]);
    };

    const removeRange = (day: TenantWeekday, index: number) => {
        setDayRanges(
            day,
            week[day].filter((_, rangeIndex) => rangeIndex !== index),
        );
    };

    const copyMondayToAllDays = () => {
        const monday = week.monday.map((range) => ({ ...range }));
        setWeek((current) => {
            const next = { ...current };
            TENANT_WEEKDAYS.forEach((day) => {
                next[day] = monday.map((range) => ({ ...range }));
            });
            return next;
        });
        setErrors([]);
        message.success(_t("openingHours.admin.copiedToAllDays"));
    };

    const addClosure = () => {
        setClosures((current) => [
            ...current,
            { id: generateRandomString(8), startDate: "", endDate: "", label: "" },
        ]);
    };

    const updateClosure = (id: string, patch: Partial<ITenantClosingDate>) => {
        setClosures((current) =>
            current.map((closure) =>
                closure.id === id ? { ...closure, ...patch } : closure,
            ),
        );
    };

    const removeClosure = (id: string) => {
        setClosures((current) => current.filter((closure) => closure.id !== id));
    };

    const handleSave = async () => {
        const weekErrors = validateWeek(week);
        setErrors(weekErrors);
        if (weekErrors.length > 0) {
            message.error(_t("openingHours.admin.fixErrors"));
            return;
        }
        if (closures.some((closure) => !closure.startDate || !closure.endDate)) {
            message.error(_t("openingHours.admin.incompleteClosure"));
            return;
        }

        setIsSaving(true);
        try {
            await onFinish({
                openingHours: { enabled, timezone, week },
                closingDates: closures.map((closure) => ({
                    ...closure,
                    label: closure.label?.trim() || undefined,
                })),
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (formLoading && !tenant) {
        return (
            <Card className="shadow-lg" style={pageStyles.stepperCard}>
                <div className="flex justify-center py-12">
                    <Spin size="large" />
                </div>
            </Card>
        );
    }

    const today = dayjs().format(DATE_FORMAT);

    return (
        <>
            <Card
                className="shadow-lg [&_.ant-card-body]:p-0"
                style={pageStyles.stepperCard}
            >
                {/* Master switch + timezone */}
                <div className="pt-8 pb-6 px-8">
                    <Title
                        level={4}
                        className="!mb-1 !font-semibold"
                        style={{ color: token.colorText }}
                    >
                        {_t("openingHours.admin.title")}
                    </Title>
                    <Text
                        className="text-sm block mb-6"
                        style={{ color: token.colorTextSecondary }}
                    >
                        {_t("openingHours.admin.subtitle")}
                    </Text>

                    <Flex align="center" gap={12} className="mb-4">
                        <Switch checked={enabled} onChange={setEnabled} />
                        <div>
                            <div style={{ fontWeight: 500 }}>
                                {_t("openingHours.admin.enable")}
                            </div>
                            <Text
                                className="text-xs"
                                style={{ color: token.colorTextSecondary }}
                            >
                                {_t("openingHours.admin.enableHelp")}
                            </Text>
                        </div>
                    </Flex>

                    <div style={{ maxWidth: 360 }}>
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                            {_t("openingHours.admin.timezone")}
                        </div>
                        <Select
                            className="w-full"
                            showSearch
                            value={timezone}
                            onChange={setTimezone}
                            options={timezoneOptions}
                        />
                        <Text
                            className="text-xs block mt-1"
                            style={{ color: token.colorTextSecondary }}
                        >
                            {_t("openingHours.admin.timezoneHelp")}
                        </Text>
                    </div>
                </div>

                <Divider className="my-0" />

                {/* Weekly schedule */}
                <div className="p-8">
                    <Flex justify="space-between" align="center" className="mb-6" wrap gap={8}>
                        <div>
                            <Title
                                level={5}
                                className="!mb-1 !font-semibold"
                                style={{ color: token.colorText }}
                            >
                                <ClockCircleOutlined className="mr-2" />
                                {_t("openingHours.admin.weeklyTitle")}
                            </Title>
                            <Text
                                className="text-sm"
                                style={{ color: token.colorTextSecondary }}
                            >
                                {_t("openingHours.admin.weeklyHelp")}
                            </Text>
                        </div>
                        <Button
                            icon={<CopyOutlined />}
                            onClick={copyMondayToAllDays}
                            disabled={week.monday.length === 0}
                        >
                            {_t("openingHours.admin.copyToAllDays")}
                        </Button>
                    </Flex>

                    {TENANT_WEEKDAYS.map((day) => {
                        const ranges = week[day];
                        const dayError = errorForDay(day);
                        return (
                            <Flex
                                key={day}
                                gap={16}
                                className="py-3"
                                style={{ borderTop: `1px solid ${token.colorBorderSecondary}` }}
                                wrap
                            >
                                <Flex align="center" gap={10} style={{ width: 170 }}>
                                    <Switch
                                        size="small"
                                        checked={ranges.length > 0}
                                        onChange={(checked) => toggleDay(day, checked)}
                                    />
                                    <span className="capitalize" style={{ fontWeight: 500 }}>
                                        {_t(`openingHours.weekdays.${day}`)}
                                    </span>
                                </Flex>

                                <Flex vertical gap={8} style={{ flex: 1, minWidth: 260 }}>
                                    {ranges.length === 0 ? (
                                        <Text style={{ color: token.colorTextTertiary }}>
                                            {_t("openingHours.closedAllDay")}
                                        </Text>
                                    ) : (
                                        ranges.map((range, index) => (
                                            <Flex key={index} align="center" gap={8}>
                                                <TimePicker.RangePicker
                                                    format={TIME_FORMAT}
                                                    minuteStep={15}
                                                    allowClear={false}
                                                    order={false}
                                                    value={[
                                                        parseTime(range.start),
                                                        parseTime(range.end),
                                                    ]}
                                                    onChange={(value) =>
                                                        updateRange(
                                                            day,
                                                            index,
                                                            value as [Dayjs | null, Dayjs | null],
                                                        )
                                                    }
                                                />
                                                <Button
                                                    type="text"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeRange(day, index)}
                                                />
                                            </Flex>
                                        ))
                                    )}

                                    {ranges.length > 0 && (
                                        <Button
                                            type="link"
                                            size="small"
                                            icon={<PlusOutlined />}
                                            className="self-start !px-0"
                                            onClick={() => addRange(day)}
                                        >
                                            {_t("openingHours.admin.addRange")}
                                        </Button>
                                    )}

                                    {dayError && (
                                        <Text type="danger" className="text-xs">
                                            {_t(`openingHours.admin.validation.${dayError.type}`)}
                                        </Text>
                                    )}
                                </Flex>
                            </Flex>
                        );
                    })}
                </div>

                <Divider className="my-0" />

                {/* Closing dates */}
                <div className="p-8">
                    <Title
                        level={5}
                        className="!mb-1 !font-semibold"
                        style={{ color: token.colorText }}
                    >
                        {_t("openingHours.admin.closuresTitle")}
                    </Title>
                    <Text
                        className="text-sm block mb-5"
                        style={{ color: token.colorTextSecondary }}
                    >
                        {_t("openingHours.admin.closuresHelp")}
                    </Text>

                    {closures.length === 0 && (
                        <Text
                            className="block mb-4"
                            style={{ color: token.colorTextTertiary }}
                        >
                            {_t("openingHours.admin.noClosures")}
                        </Text>
                    )}

                    <Flex vertical gap={10}>
                        {closures.map((closure) => {
                            const isPast = Boolean(closure.endDate) && closure.endDate < today;
                            return (
                                <Flex
                                    key={closure.id}
                                    gap={8}
                                    wrap
                                    align="center"
                                    style={{ opacity: isPast ? 0.5 : 1 }}
                                >
                                    <DatePicker.RangePicker
                                        format="DD/MM/YYYY"
                                        value={
                                            closure.startDate && closure.endDate
                                                ? [
                                                      dayjs(closure.startDate),
                                                      dayjs(closure.endDate),
                                                  ]
                                                : null
                                        }
                                        onChange={(value) => {
                                            const [start, end] = value ?? [null, null];
                                            updateClosure(closure.id, {
                                                startDate: start?.format(DATE_FORMAT) ?? "",
                                                endDate: end?.format(DATE_FORMAT) ?? "",
                                            });
                                        }}
                                    />
                                    <Input
                                        style={{ maxWidth: 260 }}
                                        maxLength={120}
                                        value={closure.label}
                                        placeholder={_t(
                                            "openingHours.admin.closureLabelPlaceholder",
                                        )}
                                        onChange={(event) =>
                                            updateClosure(closure.id, {
                                                label: event.target.value,
                                            })
                                        }
                                    />
                                    <Button
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeClosure(closure.id)}
                                    />
                                </Flex>
                            );
                        })}
                    </Flex>

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        className="mt-4"
                        onClick={addClosure}
                    >
                        {_t("openingHours.admin.addClosure")}
                    </Button>
                </div>
            </Card>

            {!enabled && (
                <Alert
                    type="info"
                    showIcon
                    className="mt-3"
                    message={_t("openingHours.admin.disabledNotice")}
                />
            )}

            <Flex className="bottom-3 z-30 mt-2 py-3 rounded-xl justify-end" gap={12}>
                <Button
                    type="primary"
                    className="flex-1 !h-11 text-base font-semibold !rounded-lg"
                    loading={isSaving}
                    onClick={handleSave}
                >
                    {_t("buttons.save")}
                </Button>
            </Flex>
        </>
    );
};
